import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { capitalizeFirstLetter } from "../../../components/utils/Date";
import { getUnauthorizedCategorySummary } from "./wasteFlowUtils";

const WasteFlowUnauthorizedCell = ({ waste, streamType }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const formatParentCategoryLabel = (categoryKey) => {
    if (!categoryKey || typeof categoryKey !== "string") return "—";
    const pluralKey = `${categoryKey}_plural`;
    const plural = t(pluralKey);
    if (plural && plural !== pluralKey) {
      return capitalizeFirstLetter(plural);
    }
    const translated = t(categoryKey);
    if (!translated || typeof translated !== "string") return categoryKey;
    return capitalizeFirstLetter(translated);
  };

  const { count, items } = getUnauthorizedCategorySummary(
    waste,
    streamType,
    formatParentCategoryLabel,
    i18n.language,
  );

  const popoverLabel =
    streamType === "hw"
      ? t("settingsWaste.colHwUnauthorized")
      : t("settingsWaste.colProUnauthorized");

  const updateMenuPosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 6,
      right: window.innerWidth - rect.right,
    });
  };

  useEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const handleOutside = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (popoverRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("mousedown", handleOutside);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open]);

  if (count === 0) {
    return <span className="waste-flow-unauthorized-cell__count">0</span>;
  }

  const popover =
    open && items.length > 0 && menuPosition
      ? createPortal(
          <div
            ref={popoverRef}
            className="waste-flow-unauthorized-cell__popover waste-flow-unauthorized-cell__popover--fixed"
            role="dialog"
            aria-label={popoverLabel}
            style={{
              top: menuPosition.top,
              right: menuPosition.right,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="waste-flow-unauthorized-cell__popover-title">
              {popoverLabel}
            </p>
            <ul className="waste-flow-unauthorized-cell__popover-list">
              {items.map((item) => (
                <li key={item.key}>{item.display}</li>
              ))}
            </ul>
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      className="waste-flow-unauthorized-cell"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        ref={triggerRef}
        type="button"
        className="waste-flow-unauthorized-cell__trigger"
        onClick={(e) => {
          e.stopPropagation();
          if (open) {
            setOpen(false);
            return;
          }
          updateMenuPosition();
          setOpen(true);
        }}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span className="waste-flow-unauthorized-cell__count">{count}</span>
        <i
          className={`fas fa-chevron-down waste-flow-unauthorized-cell__chevron ${
            open ? "waste-flow-unauthorized-cell__chevron--open" : ""
          }`}
          aria-hidden
        />
      </button>
      {popover}
    </div>
  );
};

export default WasteFlowUnauthorizedCell;
